import { observer } from 'mobx-react-lite';
import { makeAutoObservable } from 'mobx';
import React from 'react';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { GeneralDetailsState } from 'src/domains/casino/webview/bonus-engine/campaign/general-details/GeneralDetails.state';
import { Common } from 'src/AppState/Common';
import {
    BonusCampaignFormType,
    CampaignStatusType,
    CampaignUpdateFormType,
} from 'src/api_openapi/v2/bonus-engine/schemas';
import { z } from 'zod';
import { ToasterState } from 'src/AppState/ToasterState';
import { CampaignViewType as CampaignTabNameType } from 'src/domains/casino/models/models';
import { ActionConditionsState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/ActionConditions.state';
import { RewardConditionsState } from 'src/domains/casino/webview/bonus-engine/campaign/reward-conditions/RewardConditions.state';
import { PlayerManagementState } from 'src/domains/casino/webview/bonus-engine/campaign/player-management/PlayerManagement.state';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { autorun } from 'src_common/common/mobx-wrapper';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { StrictExtractType } from 'src/domains/common/types';
import { PromoCodesState } from 'src/domains/casino/webview/bonus-engine/campaign/promo-code/PromoCodes.state';
import { CampaignPopUp } from './CampaignPopup';
import {
    isError,
    isInvalidUpdateBonusCreationRes,
    isInvalidUpdateCampaignRes,
} from 'src/domains/casino/webview/bonus-engine/common/helpers';

type UpdateCampaignStatusType = StrictExtractType<CampaignStatusType, 'active' | 'paused' | 'ready'>;

export function zodGuard<T>(value: unknown, parser: z.ZodType<T>): T {
    const result = parser.safeParse(value);
    if (!result.success) {
        throw new Error(`Invalid data: ${result.error.message}`);
    }
    return result.data;
}

const registry = new FinalizationRegistry((disposers: Array<() => void>) => {
    disposers.forEach((dispose) => dispose());
});

export class CampaignCreatorState {
    public isLoading: boolean = false;
    public readonly generalDetailsState: GeneralDetailsState;
    public readonly actionConditionsState: ActionConditionsState;
    public readonly rewardConditionsState: RewardConditionsState;
    public readonly promoCodesState: PromoCodesState;
    public readonly playerManagementState: PlayerManagementState;

    private readonly campaignStore: CampaignStore;

    public static get = AutoWeakMap.create(
        (common: Common, campaignId?: string) => new CampaignCreatorState(common, campaignId)
    );

    private readonly disposers: Array<() => void> = [];

    private updateCampaignRes: unknown;

    private constructor(
        private readonly common: Common,
        private readonly campaignId?: string
    ) {
        this.campaignStore = CampaignStore.get(this.common);

        this.generalDetailsState = new GeneralDetailsState(this.common, this.campaignId);
        this.actionConditionsState = new ActionConditionsState(this.common, this.campaignId, this.generalDetailsState);
        this.rewardConditionsState = new RewardConditionsState(this.common, this.campaignId);
        this.promoCodesState = new PromoCodesState(this.common, this.campaignId);
        this.playerManagementState = new PlayerManagementState(
            this.common,
            this.promoCodesState,
            this.generalDetailsState
        );

        this.disposers.push(
            autorun(() => {
                const { formCumulative, formSingle, stakeRequirements, betResultForm } =
                    this.actionConditionsState.sportsbookSelectionsState;

                const { criteriaForm, numberOfSpins, moneteryValue } = this.actionConditionsState.casinoSelectionsState;

                const { campaignType, startTypeForm, startAtForm, frequencyPeriodForm, numberOfUsesForm } =
                    this.generalDetailsState;

                // General details
                if (startTypeForm.value === 'once-campaign-activated') {
                    startAtForm.setValue('');
                }
                if (frequencyPeriodForm.value === 'one-time') {
                    numberOfUsesForm.setValue('1');
                }

                // Promo Codes
                if (this.generalDetailsState.campaignType === 'promo-code' && this.promoCodesState.isEmpty()) {
                    this.promoCodesState.addPromoCode();
                }

                // Action Conditions
                if (this.generalDetailsState.campaignType === 'promo-code') {
                    this.actionConditionsState.bettingPlatformForm.reset();
                }
                // -- sportsbook
                if (stakeRequirements === 'cumulative') {
                    formSingle.reset();
                }
                if (stakeRequirements === 'single') {
                    formCumulative.reset();
                }
                if (campaignType !== 'settlement' && betResultForm.value !== '') {
                    betResultForm.setValue('');
                }
                // -- casino
                if (criteriaForm.value === 'min-monetary-value') {
                    numberOfSpins.reset();
                }
                if (criteriaForm.value === 'min-spins') {
                    moneteryValue.reset();
                }
            })
        );

        registry.register(this, this.disposers);
        makeAutoObservable(this);
    }

    public fillDataIf = async (): Promise<void> => {
        if (this.campaignId?.includes('default-id') === false || this.isFormTouched()) {
            await this.fillData();
        }
    };

    public CampaignConfirmationMessage = observer(() => {
        const { promoCodes, withPromoCodes } = this.promoCodeApiData;

        return (
            <CampaignPopUp
                promoCodes={promoCodes}
                withPromoCodes={withPromoCodes}
                generalDetails={this.generalDetailsState}
                playerManagement={this.playerManagementState}
                actionConditions={this.actionConditionsState}
                rewardConditions={this.rewardConditionsState}
            />
        );
    });

    private fillData = async (): Promise<void> => {
        this.isLoading = true;

        if (this.campaign === null) {
            this.isLoading = false;
            return;
        }

        await this.generalDetailsState.setFormValues(this.campaign);
        await this.actionConditionsState.setFormValues(this.campaign);
        await this.rewardConditionsState.setFormValues(this.campaign);
        await this.playerManagementState.setFormValues(this.campaign);
        this.promoCodesState.setInitialValues();

        this.isLoading = false;
        this.resetIsFormTouched();
    };

    public get campaignTabsErrors(): Record<CampaignTabNameType, number> {
        return {
            'general-details': this.generalDetailsState.formErrorsCount,
            'action-conditions': this.actionConditionsState.formErrorsCount,
            'player-management': this.playerManagementState.formErrorsCount,
            'reward-conditions': this.rewardConditionsState.formErrorsCount,
            'promo-code': this.promoCodesState.formErrorsCount,
        };
    }

    private handleCreateCampaign = async (data: BonusCampaignFormType, status: 'draft' | 'active'): Promise<void> => {
        this.isLoading = true;
        try {
            const res = await this.campaignStore.createCampaign(data);
            if (res.status === 201) {
                ToasterState.get(this.common).setToast({
                    variant: 'success',
                    title: 'Success!',
                    message:
                        status === 'draft'
                            ? 'The campaign has been successfully created as draft.'
                            : 'The campaign has been successfully created and activated.',
                });
                this.resetIsFormVisited();
                $appState.routerBackoffice.redirectToBonusCampaignList();
            } else {
                this.showErrorToaster('create', res);
            }
        } catch (_error) {
            this.showErrorToaster('create', _error);
        }
        this.isLoading = false;
        this.setIsFormTouched();
    };

    public createCampaign = async (status: 'draft' | 'active'): Promise<void> => {
        if (status === 'active') {
            await this.rewardConditionsState.verifyTotalBetPossibleValues();
        }
        this.validateCampaignForm(status);
        const { promoCodes, withPromoCodes } = this.promoCodeApiData;

        const data: BonusCampaignFormType = {
            status: status,
            name: this.generalDetailsState.name,
            description: this.generalDetailsState.termsAndConditions,
            optInOnly: this.generalDetailsState.optInOnly,
            campaignType: this.generalDetailsState.campaignType,
            startAt: this.generalDetailsState.startAt,
            expiryAt: this.generalDetailsState.expiryAt,
            frequencyPeriod: this.generalDetailsState.frequencyPeriod,
            numberOfUses: this.generalDetailsState.numberOfUses,
            actionConditions: this.actionConditionsState.actionConditions,
            bonusRewards: this.rewardConditionsState.rewardConditions,
            playerType: this.playerManagementState.playerManagement,
            startAtType: this.generalDetailsState.startType,
            maxStakeFactor: this.playerManagementState.maxSf,
            minStakeFactor: this.playerManagementState.minSf,
            eligibleUsers: this.playerManagementState.eligibleUsers,
            promoCodes,
            withPromoCodes,
            timeLimit: this.generalDetailsState.timeLimit,
            timeLimitTrigger: this.generalDetailsState.timeLimitTrigger,
        };
        const popupsStackState = PopupsStackState.get(this.common);
        const noActionCondition = this.actionConditionsState.actionConditions === undefined;

        if (status === 'active') {
            popupsStackState.addPopup('bonus_campaign_creation', {
                hasOverlay: true,
                template: 'generic',
                title: 'Confirm Creation',
                dialogClassName: noActionCondition ? 'w-[800px]' : 'w-auto',
                confirm_label: 'Confirm',
                titleClassName: 'text-center font-bold',
                cancel_label: 'Cancel',
                message: <this.CampaignConfirmationMessage />,
                onConfirm: async () => {
                    popupsStackState.closePopupOnTop();
                    await this.handleCreateCampaign(data, 'active');
                },
                onCancel: async () => {
                    popupsStackState.closePopupOnTop();
                },
            });
        } else {
            await this.handleCreateCampaign(data, 'draft');
        }
    };

    // active campaign with this method then campaign status is draft or undefined
    public activateCampaign = async (): Promise<void> => {
        await this.rewardConditionsState.verifyTotalBetPossibleValues();
        this.validateCampaignForm('active');
        const popupsStackState = PopupsStackState.get(this.common);
        popupsStackState.addPopup('bonus_campaign_activation', {
            hasOverlay: true,
            template: 'generic',
            title: 'Confirm Activation',
            dialogClassName: 'w-auto',
            confirm_label: 'Confirm',
            cancel_label: 'Cancel',
            titleClassName: 'text-center font-bold',
            message: <this.CampaignConfirmationMessage />,
            onConfirm: async () => {
                popupsStackState.closePopupOnTop();
                if (this.campaignId === undefined) {
                    return;
                }
                this.isLoading = true;
                await this.updateCampaign('active');

                if (isInvalidUpdateCampaignRes(this.updateCampaignRes) || isError(this.updateCampaignRes)) {
                    this.showErrorToaster('activate', this.updateCampaignRes);
                    this.isLoading = false;

                    return;
                }

                try {
                    const res = await this.campaignStore.activateCampaign(this.campaignId);
                    await this.fillData();
                    if (res.status === 200) {
                        ToasterState.get(this.common).setToast({
                            variant: 'success',
                            title: 'Success!',
                            message: 'The campaign has been successfully activated.',
                        });
                    } else {
                        this.showErrorToaster('activate', res);
                    }
                } catch (_error) {
                    this.showErrorToaster('activate', _error);
                }
                this.isLoading = false;
            },
            onCancel: () => {
                popupsStackState.closePopupOnTop();
            },
        });
    };

    public activateCampaignFromList = async (): Promise<void> => {
        await this.fillData();
        await this.activateCampaign();
    };

    // active campaign with this method then campaign status is paused
    public updateCampaignStatus = async (status: UpdateCampaignStatusType): Promise<void> => {
        if (this.campaignId === undefined) {
            return;
        }
        this.isLoading = true;
        try {
            const res = await this.campaignStore.updateCampaignStatus({ campaignId: this.campaignId, status });
            if (res.status === 200) {
                ToasterState.get(this.common).setToast({
                    variant: 'success',
                    title: 'Success!',
                    message: `The campaign's status has been successfully updated.`,
                });
            } else {
                this.showErrorToaster('pause', res);
            }
        } catch (_error) {
            this.showErrorToaster('pause', _error);
        }
        this.isLoading = false;
    };

    public updateCampaign = async (activeCampaignStatus?: 'active'): Promise<void> => {
        if (this.campaign === null) {
            return;
        }
        if (this.campaign.status === 'draft') {
            this.validateCampaignForm(this.campaign.status);
            const { promoCodes, withPromoCodes } = this.promoCodeApiData;
            const data: CampaignUpdateFormType = {
                status: this.campaign.status,
                name: this.generalDetailsState.name,
                description: this.generalDetailsState.termsAndConditions,
                optInOnly: this.generalDetailsState.optInOnly,
                campaignType: this.generalDetailsState.campaignType,
                startAt: this.generalDetailsState.startAt,
                expiryAt: this.generalDetailsState.expiryAt,
                frequencyPeriod: this.generalDetailsState.frequencyPeriod,
                numberOfUses: this.generalDetailsState.numberOfUses,
                actionConditions: this.actionConditionsState.actionConditions,
                bonusRewards: this.rewardConditionsState.rewardConditions,
                playerType: this.playerManagementState.playerManagement,
                maxStakeFactor: this.playerManagementState.maxSf,
                minStakeFactor: this.playerManagementState.minSf,
                eligibleUsers: this.playerManagementState.eligibleUsers,
                promoCodes,
                withPromoCodes,
                timeLimit: this.generalDetailsState.timeLimit,
                timeLimitTrigger: this.generalDetailsState.timeLimitTrigger,
            };
            this.isLoading = true;
            try {
                this.updateCampaignRes = await this.campaignStore.updateCampaign({
                    campaignId: this.campaign.id,
                    requestBody: data,
                    changeStartAt: true,
                });

                if (isInvalidUpdateCampaignRes(this.updateCampaignRes) === false) {
                    if (activeCampaignStatus !== 'active') {
                        ToasterState.get(this.common).setToast({
                            variant: 'success',
                            title: 'Success!',
                            message: 'The campaign has been successfully updated.',
                        });
                    }
                    this.resetIsFormVisited();
                } else {
                    this.showErrorToaster('update', this.updateCampaignRes);
                }
            } catch (_error) {
                this.updateCampaignRes = _error;
                this.showErrorToaster('update', _error);
            }
            this.isLoading = false;
            this.resetIsFormTouched();
        }
    };

    private validateExpiryDate = (): void => {
        this.generalDetailsState.expiryAtForm.setAsVisited();
        if (this.generalDetailsState.expiryAtForm.result.value.type !== 'ok') {
            throw ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: `The campaign could not be updated. Check the campaign's end date.`,
            });
        }
    };

    public updateCampaignEndDate = async (): Promise<void> => {
        if (this.campaign === null) {
            return;
        }
        const isStatusReadyOrActive = this.campaign.status === 'ready' || this.campaign.status === 'active';
        if (isStatusReadyOrActive || this.campaign.status === 'paused') {
            this.validateExpiryDate();
            this.isLoading = true;
            try {
                const res = await this.campaignStore.updateCampaignEndDate({
                    campaignId: this.campaign.id,
                    expiryAt: this.generalDetailsState.expiryAt ?? '',
                });
                if (res.status === 200) {
                    ToasterState.get(this.common).setToast({
                        variant: 'success',
                        title: 'Success!',
                        message: `The campaign's end date has been successfully updated.`,
                    });
                    this.resetIsFormVisited();
                    await this.fillData();
                } else {
                    ToasterState.get(this.common).setToast({
                        variant: 'error',
                        title: 'Error!',
                        message: `The campaign's end date could not be updated. An error has occurred.`,
                    });
                }
            } catch (_error) {
                ToasterState.get(this.common).setToast({
                    variant: 'error',
                    title: 'Error!',
                    message: `The campaign's end date could not be updated. Server error has occurred.`,
                });
            }
            this.isLoading = false;
            this.resetIsFormTouched();
        }
    };

    public get editionDisabled(): boolean {
        const isCloneCampaign = $appState.routerBackoffice.bonusCampaignCloneIdParam !== null;
        if (
            (this.campaign?.status === 'active' ||
                this.campaign?.status === 'paused' ||
                this.campaign?.status === 'expired' ||
                this.campaign?.status === 'ready') &&
            !isCloneCampaign
        ) {
            return true;
        }
        const { bonusViewParam } = $appState.routerBackoffice;
        if (bonusViewParam === 'action-conditions' && this.generalDetailsState.campaignType === 'promo-code') {
            return true;
        }
        return false;
    }

    public get campaign(): CampaignModel | null {
        if (this.campaignId === undefined) {
            return null;
        }
        try {
            return this.campaignStore.getCampaign(this.campaignId);
        } catch (_error) {
            this.showErrorToaster('fetch', _error);
            return null;
        }
    }

    private get promoCodeApiData(): { promoCodes: CampaignUpdateFormType['promoCodes']; withPromoCodes: boolean } {
        const promoCodesAmount = this.promoCodesState.promoCodes.length;
        return {
            promoCodes: this.promoCodesState.promoCodes,
            withPromoCodes: promoCodesAmount > 0 ? true : false,
        };
    }

    private validateCampaignForm(status: CampaignStatusType): void {
        let validGeneralDetails = true;
        let validPromoCodes = true;
        let validActionConditions = true;
        let validRewardConditions = true;
        let validPlayerManagement = true;

        if (status === 'active' || status === 'ready') {
            validGeneralDetails = this.generalDetailsState.validateForm();
            validActionConditions = this.actionConditionsState.validateForm();
            validRewardConditions = this.rewardConditionsState.validateForm();
            validPlayerManagement = this.playerManagementState.validateForm();
        }

        validPromoCodes = this.promoCodesState.validateForm();

        const isInvalid =
            validGeneralDetails === false ||
            validPromoCodes === false ||
            validActionConditions === false ||
            validRewardConditions === false ||
            validPlayerManagement === false;

        if (isInvalid) {
            throw ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: 'The campaign could not be saved. Check if all fields are filled out correctly.',
            });
        }
    }

    public isFormTouched(): boolean {
        let isTouchedGeneralDetails = false;
        let isTouchedPromoCodes = false;
        let isTouchedActionConditions = false;
        let isTouchedRewardConditions = false;
        let isTouchedPlayerManagement = false;

        isTouchedGeneralDetails = this.generalDetailsState.isTouched();
        isTouchedActionConditions = this.actionConditionsState.isTouched();
        isTouchedRewardConditions = this.rewardConditionsState.isTouched();
        isTouchedPromoCodes = this.promoCodesState.isTouched();
        isTouchedPlayerManagement = this.playerManagementState.isTouched();

        return (
            isTouchedGeneralDetails ||
            isTouchedActionConditions ||
            isTouchedRewardConditions ||
            isTouchedPromoCodes ||
            isTouchedPlayerManagement
        );
    }

    public setIsFormTouched(): void {
        this.generalDetailsState.setIsTouched();
        this.actionConditionsState.setIsTouched();
        this.rewardConditionsState.setIsTouched();
        this.promoCodesState.setIsTouched();
        this.playerManagementState.setIsTouched();
    }

    public resetIsFormTouched(): void {
        this.generalDetailsState.resetIsTouched();
        this.actionConditionsState.resetIsTouched();
        this.rewardConditionsState.resetIsTouched();
        this.promoCodesState.resetIsTouched();
        this.playerManagementState.resetIsTouched();
    }

    public resetIsFormVisited(): void {
        this.generalDetailsState.resetIsVisited();
        this.actionConditionsState.resetIsVisited();
        this.rewardConditionsState.resetIsVisited();
        this.promoCodesState.resetIsVisited();
        this.playerManagementState.resetIsVisited();
    }

    public showErrorToaster(mode: 'create' | 'update' | 'activate' | 'pause' | 'fetch', source: unknown): void {
        const modeToStr = mode === 'fetch' ? `${mode}ed` : `${mode}d`;

        const isServerError = isError(source);

        let message = String(source);

        if (isServerError) {
            message = source.message;
        } else if (isInvalidUpdateBonusCreationRes(source)) {
            message = source.body.message;
        }

        ToasterState.get(this.common).setToast({
            variant: 'error',
            title: 'Error!',
            message: (
                <>
                    <p>
                        The campaign could not be {modeToStr}. {isServerError ? 'Server' : 'An'} error has occurred.
                    </p>
                    {typeof message === 'string' ? <p>{message}</p> : null}
                </>
            ),
        });
    }
}
